import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';

/**
 * Persistent top-of-screen banner that appears whenever the browser
 * reports no connectivity. Complementary to ConnectionIndicator
 * (bottom-right pill) — together they make it impossible to miss
 * that you're working with cached data.
 *
 * Why mount/unmount instead of transform-based hide:
 *   Versiones previas mantenían el banner siempre montado y lo movían
 *   con `transform: translateY(-100%)` para ocultarlo. En iOS PWA
 *   con `apple-mobile-web-app-status-bar-style: black-translucent`,
 *   el área del notch es transparente y "muestra" el píxel debajo.
 *   El bg-amber del banner quedaba visible en esa franja del notch
 *   incluso después del transform. Reportado por Joel en pruebas
 *   de modo avión + reconexión.
 *
 *   Solución definitiva: solo renderizamos el banner cuando offline.
 *   Cuando vuelve online, el componente se desmonta del DOM
 *   completamente — imposible que quede ningún rastro visual.
 *
 *   La animación de entrada se mantiene con un keyframe inline
 *   (slide-down). Salida es instantánea, lo cual está bien:
 *   queremos cero residuo cuando vuelve la conexión.
 *
 * Why we don't rely solely on `online`/`offline` window events:
 *   - iOS Safari has had bugs where these events don't always fire
 *     when toggling airplane mode mid-session.
 *   - We also poll `navigator.onLine` every 3s as a safety net so
 *     the banner state never goes stale beyond a short window.
 *
 * The banner does NOT block any interaction — it's purely
 * informational. The app stays fully functional with cached data
 * and queued mutations (offlineMutation in `lib/offlineStorage.ts`).
 */
export function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const sync = () => setOffline(!navigator.onLine);

    // Eventos del browser — la fuente primaria.
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);

    // Polling defensivo cada 3s para iOS Safari y otros casos donde
    // los eventos no disparan. Costo trivial: leer una propiedad.
    const interval = setInterval(sync, 3000);

    // También sincronizamos al volver a la pestaña (visibilitychange)
    // y al recibir foco — momentos típicos donde el usuario regresa
    // y la conectividad puede haber cambiado sin que disparara el evento.
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);

    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
      clearInterval(interval);
    };
  }, []);

  // Cuando online, NO renderizamos nada. El componente entero sale
  // del DOM y es imposible que quede una franja, fondo o cualquier
  // residuo visual en la pantalla.
  if (!offline) return null;

  return (
    <>
      {/* Keyframe inline para que el banner se deslice desde arriba
       *  cuando aparece. No necesitamos animación de salida porque
       *  cuando online el componente se desmonta. */}
      <style>{`
        @keyframes offlineBannerSlideDown {
          from { transform: translateY(-100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        className="fixed top-0 left-0 right-0 z-[60] pointer-events-none"
        style={{
          animation: 'offlineBannerSlideDown 280ms ease-out',
        }}
      >
        <div
          className="bg-amber-500 text-amber-950 border-b-2 border-amber-700/40 shadow-xl pointer-events-auto"
          style={{
            // Padding interno para que el contenido (icono + texto)
            // se renderice DEBAJO del notch sin taparlo. El bg-amber
            // se extiende al área del notch → cuando estás offline
            // toda la franja superior se ve amarilla, super obvia.
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}
        >
          <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center gap-2 text-xs sm:text-sm font-bold">
            <WifiOff className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 leading-snug">
              <span className="font-black uppercase tracking-wider mr-1.5">Sin conexión</span>
              <span className="font-medium opacity-90">
                Viendo datos guardados. Tus cambios se sincronizarán cuando vuelva la red.
              </span>
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
